import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Calendar,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Download,
  CheckCircle2,
  User,
  CalendarDays,
  Scissors,
} from "lucide-react";
import PaymentActions from "@/components/PaymentActions";

const AppointmentsPage = () => {
  const { api, business, user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  // Track original internal_notes to avoid spurious "Notes saved" on blur
  const savedNotesRef = useRef("");

  // Filters
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    staffId: "all",
    startDate: "",
    endDate: "",
    _showFilters: false,
  });

  useEffect(() => {
    if (business?.id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    business,
    filters.startDate,
    filters.endDate,
    filters.status,
    filters.staffId,
  ]);

  // Sync savedNotesRef whenever a new appointment is opened
  useEffect(() => {
    if (selectedAppointment) {
      savedNotesRef.current = selectedAppointment.internal_notes || "";
    }
  }, [selectedAppointment]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      if (filters.status !== "all") {
        params.status = filters.status === "pending" ? "booked" : filters.status;
      }
      if (filters.staffId !== "all") params.staff_id = filters.staffId;

      const [appointmentsRes, staffRes] = await Promise.all([
        api.get(`/businesses/${business.id}/appointments`, { params }),
        user.role === "owner"
          ? api.get(`/businesses/${business.id}/staff`)
          : Promise.resolve({ data: [] }),
      ]);

      setAppointments(appointmentsRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      console.error("Failed to fetch appointments:", error);
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  // Terminal status actions — close modal immediately after API call
  const handleStatusAndClose = async (appointmentId, status) => {
    const messages = {
      completed: "Appointment marked as complete",
      no_show: "Marked as no-show",
      cancelled: "Appointment cancelled",
    };
    try {
      await api.put(
        `/businesses/${business.id}/appointments/${appointmentId}`,
        { status },
      );
      toast.success(messages[status] || `Appointment ${status}`);
      fetchData();
    } catch (error) {
      console.error("Failed to update appointment:", error);
      toast.error("Failed to update appointment");
    } finally {
      setDetailsOpen(false);
    }
  };

  // Only save notes if the value actually changed
  const updateInternalNotes = async (appointmentId, notes) => {
    if (notes === savedNotesRef.current) return;
    savedNotesRef.current = notes;
    try {
      await api.put(
        `/businesses/${business.id}/appointments/${appointmentId}`,
        {
          internal_notes: notes,
        },
      );
      toast.success("Notes saved");
    } catch (error) {
      console.error("Failed to save notes:", error);
      toast.error("Failed to save notes");
    }
  };

  const generateInvoice = async (appointmentId) => {
    setGeneratingInvoice(true);
    try {
      const response = await api.post(
        `/businesses/${business.id}/invoices`,
        { appointment_id: appointmentId, tax_rate: 0 },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${appointmentId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (error) {
      console.error("Failed to generate invoice:", error);
      toast.error("Failed to generate invoice");
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const openModal = (appt) => {
    setSelectedAppointment(appt);
    setDetailsOpen(true);
  };

  const filteredAppointments = appointments
    .filter((appt) => {
      if (filters.search) {
        const s = filters.search.toLowerCase();
        return (
          appt.customer_name.toLowerCase().includes(s) ||
          appt.customer_email.toLowerCase().includes(s) ||
          appt.service_name.toLowerCase().includes(s)
        );
      }
      return true;
    })
    .sort((a, b) => {
      const dateCompare = (b.date || "").localeCompare(a.date || "");
      if (dateCompare !== 0) return dateCompare;
      return (b.time || "").localeCompare(a.time || "");
    });

  // dd/mm/yyyy
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [y, m, d] = dateString.split("-");
    return `${d}/${m}/${y}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusConfig = (status) => {
    const configs = {
      booked: {
        label: "Pending",
        icon: Clock,
        bg: "bg-amber-100",
        text: "text-amber-700",
        dot: "bg-amber-400",
      },
      pending: {
        label: "Pending",
        icon: Clock,
        bg: "bg-amber-100",
        text: "text-amber-700",
        dot: "bg-amber-400",
      },
      cancelled: {
        label: "Cancelled",
        icon: XCircle,
        bg: "bg-red-100",
        text: "text-red-700",
        dot: "bg-red-400",
      },
      completed: {
        label: "Completed",
        icon: CheckCircle,
        bg: "bg-green-100",
        text: "text-green-700",
        dot: "bg-green-400",
      },
      no_show: {
        label: "No Show",
        icon: XCircle,
        bg: "bg-rose-100",
        text: "text-rose-700",
        dot: "bg-rose-400",
      },
    };
    return configs[status] || configs.pending;
  };

  const StatusBadge = ({ status }) => {
    const cfg = getStatusConfig(status);
    const Icon = cfg.icon;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {cfg.label}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">
            Loading appointments...
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-3 sm:space-y-6" data-testid="appointments-page">
        {/* ── Page Header — Desktop ──────────────────────────────────── */}
        <div className="hidden md:flex items-end justify-between">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">
            Appointments
          </h1>
          {/* Quick-filter status chips */}
          <div className="flex items-center gap-2">
            {["pending", "completed", "cancelled", "no_show"].map((s) => {
              const count = appointments.filter((a) =>
                s === "pending"
                  ? a.status === "booked" || a.status === "pending"
                  : a.status === s,
              ).length;
              const cfg = getStatusConfig(s);
              const active = filters.status === s;
              return (
                <button
                  key={s}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      status: active ? "all" : s,
                    }))
                  }
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    active
                      ? `${cfg.bg} ${cfg.text} border-transparent ring-2 ring-offset-1 ring-current/40`
                      : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  {cfg.label}
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? "bg-white/60" : "bg-gray-100 text-gray-600"}`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile header — compact with inline filter toggle */}
        <div className="md:hidden -mt-2">
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-lg font-extrabold">
              Appointments
            </h1>
            <button
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  _showFilters: !prev._showFilters,
                }))
              }
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50"
              data-testid="toggle-filters-mobile"
            >
              <Filter className="w-3 h-3" />
              Filters
              {(filters.status !== "all" ||
                filters.staffId !== "all" ||
                filters.startDate ||
                filters.endDate ||
                filters.search) && (
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
              )}
            </button>
          </div>
          {/* Scrollable status chips */}
          <div className="flex gap-1.5 overflow-x-auto mt-1.5 pb-1 -mx-1 px-1 no-scrollbar">
            {["all", "pending", "completed", "cancelled", "no_show"].map((s) => {
              const count =
                s === "all"
                  ? appointments.length
                  : appointments.filter((a) =>
                      s === "pending"
                        ? a.status === "booked" || a.status === "pending"
                        : a.status === s,
                    ).length;
              const cfg = s === "all" ? null : getStatusConfig(s);
              const active = filters.status === s;
              return (
                <button
                  key={s}
                  onClick={() => setFilters((prev) => ({ ...prev, status: s }))}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap shrink-0 transition-all ${
                    active
                      ? s === "all"
                        ? "bg-slate-100 text-slate-700 border-slate-200"
                        : `${cfg.bg} ${cfg.text} border-transparent`
                      : "bg-white border-gray-200 text-gray-500"
                  }`}
                >
                  {s === "all" ? "All" : cfg.label}
                  <span
                    className={`text-[10px] ${active ? "opacity-75" : "text-gray-400"}`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Filters — Mobile (collapsible) ─────────────────────────── */}
        {filters._showFilters && (
          <div className="md:hidden -mt-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2.5 space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  placeholder="Search name, email..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                  }
                  className="pl-8 h-8 text-xs rounded-lg border-gray-200"
                  data-testid="search-appointments-mobile"
                />
              </div>
              <div className="flex gap-1.5 items-center">
                {staff.length > 0 && (
                  <Select
                    value={filters.staffId}
                    onValueChange={(v) =>
                      setFilters((prev) => ({ ...prev, staffId: v }))
                    }
                  >
                    <SelectTrigger
                      className="w-[90px] shrink-0 h-7 text-[11px] rounded-lg border-gray-200 px-2"
                      data-testid="filter-staff-mobile"
                    >
                      <SelectValue placeholder="Staff" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Staff</SelectItem>
                      {staff.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name || s.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  className="flex-1 min-w-0 h-7 text-[11px] rounded-lg border-gray-200 px-1.5"
                  data-testid="filter-from-mobile"
                />
                <span className="text-gray-300 text-[10px] shrink-0">to</span>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                  className="flex-1 min-w-0 h-7 text-[11px] rounded-lg border-gray-200 px-1.5"
                  data-testid="filter-to-mobile"
                />
              </div>
            </div>
          </div>
        )}
        <div className="hidden md:block">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, or service..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                  }
                  className="pl-9 h-10 rounded-xl border-gray-200"
                  data-testid="search-appointments"
                />
              </div>
              <Select
                value={filters.status}
                onValueChange={(v) =>
                  setFilters((prev) => ({ ...prev, status: v }))
                }
              >
                <SelectTrigger
                  className="w-[155px] h-10 rounded-xl border-gray-200"
                  data-testid="filter-status"
                >
                  <Filter className="w-4 h-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="no_show">No Show</SelectItem>
                </SelectContent>
              </Select>
              {staff.length > 0 && (
                <Select
                  value={filters.staffId}
                  onValueChange={(v) =>
                    setFilters((prev) => ({ ...prev, staffId: v }))
                  }
                >
                  <SelectTrigger
                    className="w-[155px] h-10 rounded-xl border-gray-200"
                    data-testid="filter-staff"
                  >
                    <SelectValue placeholder="All Staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name || s.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  className="w-[145px] h-10 rounded-xl border-gray-200"
                  data-testid="filter-start-date"
                />
                <span className="text-gray-300 text-sm font-light">→</span>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                  className="w-[145px] h-10 rounded-xl border-gray-200"
                  data-testid="filter-end-date"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Appointments List ──────────────────────────────────────── */}
        {filteredAppointments.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="font-heading font-bold text-lg mb-2">
              No appointments found
            </h3>
            <p className="text-muted-foreground">
              Try adjusting your filters or date range.
            </p>
          </div>
        ) : (
          <>
            {/* ── Desktop Table (no Action column) ── */}
            <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/70">
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Service
                    </th>
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAppointments.map((appt, idx) => (
                    <tr
                      key={appt.id}
                      className={`cursor-pointer transition-colors hover:bg-violet-50/50 ${idx !== 0 ? "border-t border-gray-100" : ""}`}
                      onClick={() => openModal(appt)}
                    >
                      {/* Date & Time */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-violet-50 flex flex-col items-center justify-center flex-shrink-0 border border-violet-100">
                            <span className="text-[9px] font-bold text-violet-400 uppercase leading-none">
                              {new Date(appt.date).toLocaleString("en-US", {
                                month: "short",
                              })}
                            </span>
                            <span className="text-sm font-extrabold text-violet-700 leading-tight">
                              {new Date(appt.date).getDate()}
                            </span>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 text-sm">
                              {formatDate(appt.date)}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {formatTime(appt.time)} –{" "}
                              {formatTime(appt.end_time)}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Customer */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-indigo-600">
                              {appt.customer_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 text-sm">
                              {appt.customer_name}
                            </div>
                            <div className="text-xs text-gray-400 truncate max-w-[180px]">
                              {appt.customer_email}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Service */}
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900 text-sm">
                          {appt.service_name}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {appt.duration} min
                        </div>
                      </td>

                      {/* Price */}
                      <td className="px-6 py-4">
                        <span className="text-base font-bold text-gray-900">
                          ${appt.service_price.toFixed(2)}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        <StatusBadge status={appt.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Footer count */}
              <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/40">
                <span className="text-xs text-gray-400">
                  Showing{" "}
                  <span className="font-semibold text-gray-600">
                    {filteredAppointments.length}
                  </span>{" "}
                  appointment{filteredAppointments.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>

            {/* ── Mobile Card View (unchanged) ── */}
            <div className="md:hidden space-y-4">
              {filteredAppointments.map((appt) => (
                <div
                  key={appt.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openModal(appt)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-bold text-lg text-gray-900">
                        {formatDate(appt.date)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatTime(appt.time)} - {formatTime(appt.end_time)}
                      </div>
                    </div>
                    <StatusBadge status={appt.status} />
                  </div>
                  <div className="border-t border-gray-200 my-3"></div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Customer</div>
                      <div className="font-semibold text-gray-900">
                        {appt.customer_name}
                      </div>
                      <div className="text-sm text-gray-500 break-all">
                        {appt.customer_email}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Service</div>
                      <div className="font-semibold text-gray-900">
                        {appt.service_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {appt.duration} min
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <div className="text-2xl font-bold text-gray-900">
                      ${appt.service_price.toFixed(2)}
                    </div>
                    <Button
                      size="sm"
                      className="bg-primary hover:bg-primary/90 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(appt);
                      }}
                    >
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Appointment Details Modal ──────────────────────────────── */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent
            className="max-w-lg w-full flex flex-col max-h-[100dvh] sm:max-h-[90vh] overflow-hidden p-0 gap-0 rounded-2xl"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              event.currentTarget.focus({ preventScroll: true });
            }}
          >
            {/* Pinned header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-gray-900">
                  Appointment Details
                </DialogTitle>
              </DialogHeader>
              {selectedAppointment && (
                <div className="flex items-center gap-3 mt-2">
                  <StatusBadge status={selectedAppointment.status} />
                  <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded-md">
                    #{selectedAppointment.id.slice(0, 8)}
                  </span>
                </div>
              )}
            </div>

            {/* Scrollable body */}
            {selectedAppointment && (
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
                {/* Customer card */}
                <div className="rounded-xl border border-border bg-muted/30 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
                    <User className="w-3 h-3" /> Customer
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary font-bold text-base">
                      {selectedAppointment.customer_name
                        ?.charAt(0)
                        .toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {selectedAppointment.customer_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {selectedAppointment.customer_email}
                      </p>
                      {selectedAppointment.customer_phone && (
                        <p className="text-sm text-gray-500">
                          {selectedAppointment.customer_phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Service + Schedule side-by-side */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-border bg-muted/30 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      Service
                    </p>
                    <p className="font-semibold text-sm leading-snug">
                      {selectedAppointment.service_name}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                      <Clock className="w-3.5 h-3.5 shrink-0" />
                      <span>{selectedAppointment.duration} min</span>
                      <span className="font-medium text-foreground">
                        · $
                        {selectedAppointment.service_price?.toFixed(2) ?? "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="rounded-xl border border-border bg-muted/30 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      Schedule
                    </p>
                    <p className="font-semibold text-sm leading-snug">
                      {formatDateDisplay(selectedAppointment.date)}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
                      <Clock className="w-3.5 h-3.5 shrink-0" />
                      <span>
                        {selectedAppointment.time} –{" "}
                        {selectedAppointment.end_time}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Customer booking notes (read-only) */}
                {selectedAppointment.notes && (
                  <div className="rounded-xl border border-border bg-muted/30 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Customer Notes
                    </p>
                    <p className="text-sm text-foreground">
                      {selectedAppointment.notes}
                    </p>
                  </div>
                )}

                {/* Internal notes */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Internal Notes
                  </label>
                  <textarea
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[90px] placeholder:text-muted-foreground"
                    rows={3}
                    placeholder="Add private notes about this appointment..."
                    defaultValue={selectedAppointment.internal_notes || ""}
                    onBlur={(e) =>
                      updateInternalNotes(
                        selectedAppointment.id,
                        e.target.value,
                      )
                    }
                  />
                </div>

                {/* Payment Actions */}
                <PaymentActions
                  appointment={selectedAppointment}
                  api={api}
                  onUpdate={() => {
                    fetchAppointments();
                    setDetailsOpen(false);
                  }}
                />
              </div>
            )}

            {/* Pinned footer — action buttons */}
            {selectedAppointment && (
              <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 flex flex-col sm:flex-row gap-2">
                {selectedAppointment.status === "booked" && (
                  <>
                    <Button
                      className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold gap-2"
                      onClick={() =>
                        handleStatusAndClose(
                          selectedAppointment.id,
                          "completed",
                        )
                      }
                      data-testid="complete-appointment-btn"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Mark Complete
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 h-11 rounded-xl font-semibold gap-2 border-gray-200 text-gray-700"
                      onClick={() =>
                        handleStatusAndClose(selectedAppointment.id, "no_show")
                      }
                    >
                      <Clock className="w-4 h-4" />
                      No Show
                    </Button>
                  </>
                )}
                {selectedAppointment.status !== "cancelled" &&
                  selectedAppointment.status !== "completed" &&
                  selectedAppointment.status !== "no_show" && (
                    <Button
                      variant="outline"
                      className="flex-1 h-11 rounded-xl font-semibold gap-2 border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() =>
                        handleStatusAndClose(
                          selectedAppointment.id,
                          "cancelled",
                        )
                      }
                      data-testid="cancel-appointment-btn"
                    >
                      <XCircle className="w-4 h-4" />
                      Cancel
                    </Button>
                  )}
                {selectedAppointment.status === "completed" && (
                  <Button
                    variant="outline"
                    className="flex-1 h-11 rounded-xl font-semibold gap-2 border-gray-200 text-gray-700"
                    onClick={() => generateInvoice(selectedAppointment.id)}
                    disabled={generatingInvoice}
                    data-testid="generate-invoice-btn"
                  >
                    {generatingInvoice ? (
                      "Generating..."
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Generate Invoice
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AppointmentsPage;
